import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import { useSidebar } from '@/contexts/SidebarContext';
import CommentsSection from '@/components/comments/CommentsSection';
import { useSubscription } from '../../../services/billing';
import { FEATURE, planHasFeature } from '@/components/settings/tiers/tierCatalog';
import { useCourse, useUpdateCourse, useDeleteCourse } from '../../../services/courses';
import type { WizardStepNumber } from './wizardTypes';
import { useWizardState } from './useWizardState';
import type { WizardInitialData } from './useWizardState';
import WizardStepIndicator from './WizardStepIndicator';
import CourseDetailsStep from './steps/CourseDetailsStep';
import CourseContentStep from './steps/CourseContentStep';
import CoursePreviewOverlay from './shared/CoursePreviewOverlay';
import PublishSwapDialog from '../../../components/courses/PublishSwapDialog';
// GamificationStep will be created soon — import already declared
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import GamificationStep from './steps/GamificationStep';
import CertificateStep from './steps/CertificateStep';
import SettingsIcon from '@/components/icons/SettingsIcon';
import PencilIcon from '@/components/icons/PencilIcon';
import VerifiedIcon from '@/components/icons/VerifiedIcon';
import { useDismissible } from '@/hooks/learner/useMemberConfig';

// ─── Icons ───────────────────────────────────────────────────────────────────

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
    aria-hidden="true"
  >
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16 }}
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16 }}
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    style={{ width: 14, height: 14 }}
    className="animate-spin"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
    <path d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

// ─── Bottom Reminder Banner ───────────────────────────────────────────────────

interface BottomReminderProps {
  courseId: string;
  isPublished?: boolean;
}

function BottomReminder({ courseId, isPublished = false }: BottomReminderProps) {
  const navigate = useNavigate();
  const { dismissed, isLoading, dismiss } = useDismissible('wizard-bottom-reminder-dismissed');
  // Preview opens as an in-place overlay — the wizard stays mounted underneath,
  // so closing it returns to the exact same progress (no navigation, no reset).
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <div className="bg-surface-elevated rounded-2xl border border-border-primary shadow-[0_4px_24px_rgba(71,70,76,0.08)] px-8 py-7">
      {/* Dismissible explanatory note. Only this collapses on "never show it
          again" — the action buttons below stay accessible regardless. */}
      <AnimatePresence initial={false}>
        {!isLoading && !dismissed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            {/* Header row */}
            <div className="flex justify-between items-start mb-3">
              <span className="text-base font-semibold text-text-primary tracking-tight">
                Reminder
              </span>
              <button
                onClick={dismiss}
                className="text-[13px] font-medium text-primary-500 underline decoration-dotted hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
              >
                Never show it again
              </button>
            </div>

            {/* Message */}
            <p className="text-sm text-text-secondary leading-relaxed mb-7 max-w-[640px]">
              {isPublished ? (
                <>
                  Your changes are saved. Learners who already have this
                  course will see the updated content. To share it with more
                  people, click <strong>Invite learners now</strong>.
                </>
              ) : (
                <>
                  Your course is saved as a draft — learners can&rsquo;t see
                  it yet. Click <strong>Invite learners now</strong> to choose
                  who gets it and publish. You can also do this anytime from
                  the Course Library: open your course and pick Configure.
                </>
              )}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action buttons — always available, independent of the reminder note. */}
      <div className="flex flex-wrap items-center justify-center gap-8">
        {/* The golden path: a created course wants learners next. */}
        <button
          onClick={() => navigate(`/courses/${courseId}/configure`)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold transition-colors"
          data-testid="wizard-invite-learners"
        >
          Invite learners now
          <ArrowRightIcon />
        </button>

        {/* Preview */}
        <button
          onClick={() => setPreviewOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-[0.7px] border-text-primary text-text-primary dark:border-text-primary/30 text-sm font-bold hover:bg-neutral-100 dark:hover:bg-surface-primary transition-colors"
        >
          <EyeIcon />
          Preview whole course
        </button>

        {/* Park it as a draft */}
        <button
          onClick={() => navigate('/courses?status=draft')}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary hover:opacity-90 text-white text-sm font-bold transition-opacity"
        >
          Keep as draft for now
        </button>
      </div>

      <CoursePreviewOverlay
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        courseId={courseId}
      />
    </div>
  );
}

// ─── Disabled overlay wrapper ─────────────────────────────────────────────────

interface DisabledWrapperProps {
  disabled: boolean;
  children: React.ReactNode;
}

function DisabledWrapper({ disabled, children }: DisabledWrapperProps) {
  if (!disabled) return <>{children}</>;
  return (
    <div className="relative">
      {children}
      <div className="absolute inset-0 rounded-2xl bg-surface-elevated/70 backdrop-blur-sm cursor-not-allowed z-10 flex items-center justify-center">
        <span className="text-[13px] font-medium text-text-secondary bg-surface-elevated border border-border-primary/20 rounded-lg px-3.5 py-1.5 shadow-sm">
          Complete Step 1 first
        </span>
      </div>
    </div>
  );
}

// ─── Placeholder cards (rendered inside DisabledWrapper when courseId is null) ─

function LockedStepCard({ number, label, description }: { number: number; label: string; description: string }) {
  return (
    <div className="bg-surface-elevated rounded-2xl border border-border-primary p-7 min-h-[100px] flex items-center gap-4">
      <div className="w-9 h-9 rounded-full bg-surface-primary flex items-center justify-center text-sm font-bold text-neutral-400 shrink-0">
        {number}
      </div>
      <div>
        <div className="text-[15px] font-semibold text-neutral-400 mb-1">{label}</div>
        <div className="text-[13px] text-neutral-300">{description}</div>
      </div>
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

interface CourseCreationWizardProps {
  /** Pass initial data for edit mode. If omitted, wizard runs in create mode. */
  initialData?: WizardInitialData;
}

export default function CourseCreationWizard({ initialData }: CourseCreationWizardProps) {
  const navigate = useNavigate();
  const { setCollapsed: setSidebarCollapsed } = useSidebar();
  const { data: subscription } = useSubscription();
  const currentTier = subscription?.plan_id?.toLowerCase() ?? '';
  // Gamification follows the leaderboard (Growth+), not the prize shop —
  // see the note in GamificationStep (Aleix, 2026-09-04).
  const gamificationEnabled = planHasFeature(FEATURE.leaderboard, currentTier);
  // Free has no certificates at all — its step leaves the bar entirely
  // (the locked card below still explains the upgrade, like gamification).
  const certificationEnabled = planHasFeature(FEATURE.certificateTemplate, currentTier);
  const { state, setCourseId, markStepSaved, isStepEnabled } =
    useWizardState(initialData);
  // Plan-locked steps render an upgrade card with no Save button — they must
  // not gate completion, or lower plans can never reach the finish controls.
  const requiredStepsSaved =
    state.steps[1].status === 'saved' &&
    state.steps[2].status === 'saved' &&
    (!gamificationEnabled || state.steps[3].status === 'saved') &&
    (!certificationEnabled || state.steps[4].status === 'saved');
  const reduceMotion = useReducedMotion();
  const stepDelay = (i: number) => (reduceMotion ? 0 : i * 0.06);

  // Edit-mode publish controls — only meaningful when an existing course is loaded.
  const { data: courseData } = useCourse(state.courseId ?? undefined);
  const updateCourse = useUpdateCourse();
  const deleteCourse = useDeleteCourse();
  const [discarding, setDiscarding] = useState(false);

  // Change-of-mind escape hatch (create mode): the wizard persists a draft as
  // soon as step 1 saves, so backing out must offer deleting it — not silently
  // leave a stray draft in the library.
  const handleDiscard = async () => {
    if (!window.confirm('Delete this course and everything you added to it? This cannot be undone.')) {
      return;
    }
    setDiscarding(true);
    try {
      if (state.courseId) await deleteCourse.mutateAsync(state.courseId);
      toast('Course discarded', { icon: '🗑️' });
      navigate('/courses');
    } catch {
      toast.error('Could not delete the course. Please try again.');
      setDiscarding(false);
    }
  };
  const courseStatus = courseData?.status;
  const isPublished = courseStatus === 'published';

  // Plan cap on publish: instead of a dead-end error, offer the swap dialog
  // (pick a published course to park; Aleix, 2026-08-10).
  const [swapLimitMessage, setSwapLimitMessage] = useState<string | null>(null);

  const setStatus = useCallback(
    async (next: 'published' | 'draft') => {
      if (!state.courseId) return;
      // Unpublishing hides the course from every enrolled learner mid-stream —
      // say so before doing it (Aleix, 2026-08-23). Progress is never lost.
      if (next === 'draft') {
        const n = courseData?.enrolled_learner_count ?? 0;
        const impact = n > 0
          ? `${n} enrolled learner${n === 1 ? '' : 's'} will lose access to it until it is published again (their progress is kept).`
          : 'Learners will not see it until it is published again.';
        if (!window.confirm(`Move “${courseData?.title ?? 'this course'}” back to draft? ${impact}`)) {
          return;
        }
      }
      try {
        await updateCourse.mutateAsync({ courseId: state.courseId, data: { status: next } });
        toast.success(
          next === 'published'
            ? 'Course published — learners can now see it.'
            : 'Moved back to draft — hidden from learners.'
        );
      } catch (err) {
        const data = (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data;
        if (next === 'published' && data?.error === 'PLAN_LIMIT_REACHED') {
          setSwapLimitMessage(data.message ?? 'Your plan is at its published-course limit.');
          return;
        }
        toast.error('Could not update course status. Please try again.');
      }
    },
    [state.courseId, updateCourse, courseData]
  );

  // Auto-collapse sidebar when entering the wizard
  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  // ── Scroll-based focus tracking ──
  const [focusedStep, setFocusedStep] = useState<WizardStepNumber>(1);
  const stepRefs = useRef<Record<WizardStepNumber, HTMLDivElement | null>>({
    1: null, 2: null, 3: null, 4: null,
  });

  const setStepRef = useCallback((step: WizardStepNumber) => (el: HTMLDivElement | null) => {
    stepRefs.current[step] = el;
  }, []);

  useEffect(() => {
    // Focus the step closest to the viewport's vertical center. This avoids
    // the previous "is the bottom past the sticky header?" rule which kicked
    // in too aggressively at the boundary between cards.
    const handleScroll = () => {
      const refs = stepRefs.current;
      const viewportCenter = window.innerHeight / 2;
      let best: WizardStepNumber = 1;
      let bestDistance = Infinity;
      for (const step of [1, 2, 3, 4] as WizardStepNumber[]) {
        const el = refs[step];
        if (!el) continue;
        const { top, bottom } = el.getBoundingClientRect();
        const stepCenter = (top + bottom) / 2;
        const distance = Math.abs(stepCenter - viewportCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = step;
        }
      }
      setFocusedStep(best);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll(); // initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [state.courseId]);

  const handleStep1Saved = (courseId: string) => {
    setCourseId(courseId);
    markStepSaved(1);
  };

  const step2Enabled = isStepEnabled(2);
  const step3Enabled = isStepEnabled(3);
  const step4Enabled = isStepEnabled(4);

  return (
    // The wizard now follows the active theme — semantic tokens flip the canvas,
    // step cards, indicator, and inputs between light and dark automatically.
    <div className="min-h-screen bg-background-primary">
      <Toaster position="top-right" />

      {swapLimitMessage && state.courseId && (
        <PublishSwapDialog
          courseId={state.courseId}
          courseTitle={courseData?.title ?? 'this course'}
          limitMessage={swapLimitMessage}
          onClose={() => setSwapLimitMessage(null)}
        />
      )}

      {/* Sticky step indicator */}
      <WizardStepIndicator
        steps={state.steps}
        focusedStep={focusedStep}
        gamificationEnabled={gamificationEnabled}
        certificationEnabled={certificationEnabled}
      />

      {/* Edit-mode toolbar: status controls + jump to /configure */}
      {initialData && state.courseId && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : 0.08 }}
          className="max-w-[1080px] mx-auto px-6 pt-6 flex items-center justify-between gap-2"
        >
          {/* Back to the method picker: choosing scratch / draft / Teri used to
              be a one-way door — changing your mind meant starting the whole
              "Create course" flow again (Aleix, 2026-09-15). The picker itself
              leads on to the library. */}
          <button
            type="button"
            onClick={() => navigate('/courses/create')}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-grey-900 border border-grey-900/30 dark:text-text-primary dark:border-text-primary/30 hover:bg-grey-900/[0.06] dark:hover:bg-text-primary/[0.08] active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            ← Back to course creation
          </button>

          <div className="flex items-center gap-2">
          {/* Full preview — opens the course exactly as a learner sees it */}
          <button
            type="button"
            onClick={() => navigate(`/courses/${state.courseId}/preview`)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-grey-900 border border-grey-900/30 dark:text-text-primary dark:border-text-primary/30 hover:bg-grey-900/[0.06] dark:hover:bg-text-primary/[0.08] active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            <EyeIcon />
            Full preview
          </button>

          {/* Publish — visible when the course isn't already live */}
          {courseStatus && !isPublished && (
            <button
              type="button"
              onClick={() => setStatus('published')}
              disabled={updateCourse.isPending}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-white bg-success-500 hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {updateCourse.isPending ? <SpinnerIcon /> : <VerifiedIcon className="w-4 h-4 [&_path]:fill-current" />}
              Publish
            </button>
          )}

          {/* Set to draft — visible when the course is currently published */}
          {isPublished && (
            <button
              type="button"
              onClick={() => setStatus('draft')}
              disabled={updateCourse.isPending}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-grey-900 border border-grey-900/30 dark:text-text-primary dark:border-text-primary/30 hover:bg-grey-900/[0.06] dark:hover:bg-text-primary/[0.08] active:scale-[0.97] transition-[background-color,transform] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {updateCourse.isPending ? <SpinnerIcon /> : <PencilIcon className="w-4 h-4" color="currentColor" accentColor="currentColor" />}
              Set to draft
            </button>
          )}

          {/* Configure — full assignment / scheduling flow */}
          <button
            type="button"
            onClick={() => navigate(`/courses/${state.courseId}/configure`)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-white transition-[filter,transform] duration-150 hover:brightness-105 active:scale-[0.97]"
            style={{
              background: `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)`,
            }}
          >
            <SettingsIcon className="w-4 h-4" color="currentColor" accentColor="currentColor" />
            Configure
          </button>
          </div>
        </motion.div>
      )}

      {/* Create-mode toolbar: both ways out are explicit — keep the draft, or
          delete it and return to the library. */}
      {!initialData && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : 0.08 }}
          className="max-w-[1080px] mx-auto px-6 pt-6 flex items-center justify-between gap-2"
        >
          {/* Nothing written yet → back to the method picker, where the
              decision was made. Once there is a draft it lives in the library,
              so that is where leaving takes you (Aleix, 2026-09-15). */}
          <button
            type="button"
            onClick={() => navigate(state.courseId ? '/courses' : '/courses/create')}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-grey-900 border border-grey-900/30 dark:text-text-primary dark:border-text-primary/30 hover:bg-grey-900/[0.06] dark:hover:bg-text-primary/[0.08] active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            {state.courseId ? '← Keep draft & exit' : '← Back to course creation'}
          </button>

          {state.courseId && (
            <button
              type="button"
              onClick={handleDiscard}
              disabled={discarding}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-error-600 dark:text-error-400 border border-error-500/40 hover:bg-error-500/[0.07] active:scale-[0.97] transition-[background-color,transform] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {discarding ? <SpinnerIcon /> : <TrashIcon />}
              Discard course
            </button>
          )}
        </motion.div>
      )}

      {/* Scrollable content — all 4 cards always visible */}
      <div className="max-w-[1080px] mx-auto px-6 pt-8 pb-20 flex flex-col gap-10">
        {/* Step 1: Basics — always enabled */}
        <div ref={setStepRef(1)} data-wizard-step="1">
          <CourseDetailsStep
            courseId={state.courseId}
            initialData={initialData ? {
              title: initialData.title ?? '',
              description: initialData.description ?? null,
              image_url: initialData.image_url ?? null,
              estimated_duration_minutes: initialData.estimated_duration_minutes ?? null,
              layout: initialData.layout ?? 'cards',
            } : undefined}
            onSaved={handleStep1Saved}
            onCancel={() => navigate(initialData ? `/courses/${state.courseId}` : '/courses')}
            entranceDelay={stepDelay(0)}
          />
        </div>

        {/* Step 2: Content */}
        <div ref={setStepRef(2)} data-wizard-step="2">
          <DisabledWrapper disabled={!step2Enabled}>
            {state.courseId ? (
              <CourseContentStep
                courseId={state.courseId}
                onSaved={() => markStepSaved(2)}
                entranceDelay={stepDelay(1)}
                editMode={!!initialData}
              />
            ) : (
              <LockedStepCard
                number={2}
                label="Content"
                description="Complete Step 1 to unlock course content editing."
              />
            )}
          </DisabledWrapper>
        </div>

        {/* Step 3: Gamification */}
        <div ref={setStepRef(3)} data-wizard-step="3">
          <DisabledWrapper disabled={!step3Enabled}>
            {state.courseId ? (
              <GamificationStep
                courseId={state.courseId}
                disabled={!step3Enabled}
                onSaved={() => markStepSaved(3)}
                entranceDelay={stepDelay(2)}
                editMode={!!initialData}
              />
            ) : (
              <LockedStepCard
                number={3}
                label="Gamification (optional)"
                description="Complete Step 1 to unlock gamification settings."
              />
            )}
          </DisabledWrapper>
        </div>

        {/* Step 4: Certification */}
        <div ref={setStepRef(4)} data-wizard-step="4">
          <DisabledWrapper disabled={!step4Enabled}>
            {state.courseId ? (
              <CertificateStep
                courseId={state.courseId}
                disabled={!step4Enabled}
                onSaved={() => markStepSaved(4)}
                entranceDelay={stepDelay(3)}
                editMode={!!initialData}
                initialCertificationType={initialData?.certification_type ?? null}
                initialCertificateFileKey={initialData?.certificate_file_key ?? null}
              />
            ) : (
              <LockedStepCard
                number={4}
                label="Certification (optional)"
                description="Complete Step 1 to unlock certification settings."
              />
            )}
          </DisabledWrapper>
        </div>

        {/* Bottom reminder — visible once every plan-available step is saved */}
        {requiredStepsSaved && state.courseId && (
          <BottomReminder courseId={state.courseId} isPublished={isPublished} />
        )}

        {/* What learners are saying about this course. It was visible only on
            the learner's own page, so the people who could act on it — the
            author, the trainer — never saw a word (Aleix, 2026-09-15). */}
        {state.courseId && (
          <div className="mt-10">
            <CommentsSection targetType="course" targetId={state.courseId} />
          </div>
        )}
      </div>
    </div>
  );
}
